import React from 'react';
import { Link } from 'react-router-dom';

const MessageList = ({ messages, campaignId, canAddMessages, onMessageUpdate, campaign }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Use the same logic as CampaignDetails for consistency
  const canAddMessagesBasedOnCampaign = (campaign) => {
    if (!campaign) return false;
    
    const now = new Date();
    const endDate = campaign.endTime ? new Date(campaign.endTime) : null;
    
    // Only hide the button if campaign has ended (has an end date and current time is past it)
    return !endDate || now <= endDate;
  };

  // Use campaign-based logic if available, otherwise fall back to prop
  const canAdd = campaign ? canAddMessagesBasedOnCampaign(campaign) : canAddMessages;

  const canEditMessage = (message) => {
    const now = new Date();
    const scheduledTime = new Date(message.scheduledTime);
    return now < scheduledTime;
  };

  const getMediumIcon = (medium) => {
    switch (medium.toLowerCase()) {
      case 'whatsapp':
        return '📱';
      case 'insta':
      case 'instagram':
        return '📸';
      case 'fb':
      case 'facebook':
        return '👥';
      case 'email':
        return '📧';
      default:
        return '📨';
    }
  };

  const getMediumDisplayName = (medium) => {
    switch (medium.toLowerCase()) {
      case 'whatsapp':
        return 'WhatsApp';
      case 'insta':
        return 'Instagram';
      case 'fb':
        return 'Facebook';
      case 'email':
        return 'Email';
      default:
        return medium;
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Messages</h3>
        {canAdd && (
          <Link 
            to={`/campaigns/${campaignId}/messages/new`} 
            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            + Add Message
          </Link>
        )}
      </div>

      {messages.length === 0 ? (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No messages</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by creating a new message.</p>
          {canAdd && (
            <div className="mt-6">
              <Link 
                to={`/campaigns/${campaignId}/messages/new`} 
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                + Add First Message
              </Link>
            </div>
          )}
        </div>
      ) : (
        <div className="overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Target Group
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Message Template
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Schedule
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {messages.map((message) => {
                const canEdit = canEditMessage(message);
                
                return (
                  <tr key={message.cmpMsgId} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="text-lg mr-2">
                          {getMediumIcon(message.medium)}
                        </span>
                        <span className="text-sm font-medium text-gray-900">
                          {getMediumDisplayName(message.medium)}
                        </span>
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {message.contactGroupName || message.contactGroupId || 'No group assigned'}
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {message.messageTemplateName ? (
                        <Link 
                          to={`/templates/${message.messageTemplateId}`}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          {message.messageTemplateName}
                        </Link>
                      ) : (
                        <span className="text-gray-400">No template</span>
                      )}
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {message.scheduledTime ? formatDate(message.scheduledTime) : 'Not scheduled'}
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <Link 
                          to={`/campaigns/${campaignId}/messages/${message.cmpMsgId}`}
                          className="text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded-md text-xs font-medium transition-colors"
                        >
                          View
                        </Link>
                        
                        {canEdit && (
                          <Link 
                            to={`/campaigns/${campaignId}/messages/${message.cmpMsgId}/edit`}
                            className="text-blue-600 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-md text-xs font-medium transition-colors"
                          >
                            Edit
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default MessageList;
